import { type ModalWidth } from "components/modal";
import React, { useRef } from "react";
import { Modal } from "components";
import { type RemovableProps } from "components/Removables/Removable";
import { ModalPickerProps } from "./props";
import { useValidateIsNotNull } from "formvalidation/hooks";
import { SelectOrNew } from "./SelectOrNew";

interface Props<T> extends ModalPickerProps<T> {
  buttonLabel: string
  modalTitle: string
  modalSize: ModalWidth
  modalHeight?: number

  onNewClicked: () => void
  onModalClose?: () => void

  renderModalContent: (closeModal: () => void) => JSX.Element
  renderRemovable: (props: RemovableProps<T>) => JSX.Element
}

const ModalPickerWithChooseButton = <T extends unknown>(props: Props<T>) => {
  const { canCreateNew = false } = props;

  const startOpen = props.inPageEditing != null
    ? props.inPageEditing.hasValue
    : (props.nonInPageEditing?.startOpen ?? false);
  const value = props.nonInPageEditing?.value ?? undefined as unknown as T;
  const required = props.nonInPageEditing?.required ?? false;
  const validatorKey = props.nonInPageEditing?.validatorKey ?? undefined;
  const legacyHasError = props.nonInPageEditing?.legacyHasError ?? undefined;

  const [isModalOpen, setIsModalOpen] = React.useState(startOpen);
  const [modalJustClosed, setModalJustClosed] = React.useState(false);

  const onChange = (newValue: T) => {
    props.nonInPageEditing?.onChange(newValue);
    if (newValue == null && required) {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalJustClosed(true);
    setTimeout(() => {
      setModalJustClosed(false);
    }, 2500);
    props.onModalClose?.();
  };

  const cancel = () => {
    props.inPageEditing?.cancel();
    closeModal();
  }

  const hasValue = Boolean(value);

  const ref = useRef<HTMLDivElement>(null);

  let hasError: boolean = false;

  if (legacyHasError != null) {
    hasError = legacyHasError;
  }
  else if (required && legacyHasError == null) {
    const validation = useValidateIsNotNull({
      key: validatorKey,
      title: validatorKey,
      value: value,
      ref
    });

    hasError = validation.error.hasError;
  }

  return <>
    <div ref={ref}></div>
    {!hasValue && <div className="flex">
      <SelectOrNew
        canCreateNew={canCreateNew}
        newButton={{
          label: RESX.Buttons.btnNew,
          onClick: props.onNewClicked
        }}
        selectButton={{
          label: props.buttonLabel,
          onClick: () => { setIsModalOpen(true); }
        }}
        hasError={hasError}
      />
    </div>}

    {hasValue && props.renderRemovable({ autoFocus: modalJustClosed, value: value, onChange, readonly: false, error: hasError })}

    <Modal
      isOpen={isModalOpen}
      handleClose={cancel}
      width={props.modalSize}
      gap="regular"
      title={props.modalTitle}
      height={props.modalHeight ?? 500}
    >
      {props.renderModalContent(closeModal)}
    </Modal>
  </>;
};

export default ModalPickerWithChooseButton;